import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { apiFetch } from "../lib/api";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "canvasser";
  campaignId: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export type LoginResult =
  | { type: "success"; user: AuthUser }
  | { type: "new_password_required"; session: string; email: string }
  | { type: "error"; message: string };

interface MeResponse {
  user: AuthUser;
}

interface LoginResponse {
  error?: string;
  challenge?: string;
  session?: string;
  email?: string;
  user?: AuthUser;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch("/api/auth/me");
      if (res.ok) {
        const data = (await res.json()) as MeResponse;
        setState({ user: data.user, loading: false });
      } else {
        setState({ user: null, loading: false });
      }
    } catch {
      setState({ user: null, loading: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const res = await apiFetch("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password }),
        });

        const data = (await res.json()) as LoginResponse;

        if (!res.ok) {
          return { type: "error", message: data.error ?? "Login failed" };
        }

        if (data.challenge === "NEW_PASSWORD_REQUIRED") {
          return {
            type: "new_password_required",
            session: data.session ?? "",
            email: data.email ?? email,
          };
        }

        if (!data.user) {
          return { type: "error", message: "Login failed" };
        }

        setState({ user: data.user, loading: false });
        return { type: "success", user: data.user };
      } catch {
        return { type: "error", message: "Network error. Please try again." };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await apiFetch("/api/auth/logout", { method: "POST" });
    setState({ user: null, loading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
